import * as React from "react";
import { cn } from "../../lib/utils";

// 입력 필드 프롭스 인터페이스
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * 에러 상태. string이면 메시지를 자동 렌더하고, boolean이면 aria-invalid만 표시.
   */
  error?: string | boolean;
  /**
   * 외부에서 에러 컨테이너 id를 지정하고 싶을 때 사용. 지정 없으면 useId로 자동 생성.
   */
  errorId?: string;
  /**
   * 추가적인 설명 텍스트 id (에러와는 별도로 aria-describedby에 포함됨)
   */
  describedBy?: string;
}

/**
 * 입력 필드 컴포넌트
 * - 기본 스타일이 적용된 입력 필드
 * - error prop으로 WCAG 3.3.1 (Error Identification) / 4.1.3 (Status Messages) 자동 준수
 * - aria-invalid, aria-describedby, role="alert" 자동 연결
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, errorId, describedBy, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
    const autoId = React.useId();
    const hasError = error !== undefined && error !== false && error !== null && error !== '';
    const isStringError = typeof error === 'string' && error.length > 0;
    const resolvedErrorId = errorId ?? (hasError ? `${autoId}-error` : undefined);

    // aria-describedby 병합: 에러 id + describedBy prop + 기존 aria-describedby
    const describedByIds = [
      hasError ? resolvedErrorId : undefined,
      describedBy,
      ariaDescribedBy,
    ].filter(Boolean).join(' ') || undefined;

    return (
      <>
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors",
            "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium",
            "placeholder:text-gray-500 dark:placeholder:text-slate-300",
            "disabled:text-gray-500 dark:disabled:text-slate-400",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:border-primary-500 dark:focus-visible:ring-offset-slate-900",
            "disabled:cursor-not-allowed disabled:opacity-50",
            hasError && "border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500 dark:border-red-400",
            className
          )}
          ref={ref}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={describedByIds}
          {...props}
        />
        {isStringError && (
          <p
            id={resolvedErrorId}
            role="alert"
            aria-live="polite"
            className="mt-1 text-sm text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        )}
      </>
    );
  }
);

Input.displayName = "Input";

export { Input };
